import { defineStore } from "pinia";

export type UiScale = "compact" | "standard";
export type UiTheme = "light" | "dark" | "warm";

const SCALE_ZOOM: Record<UiScale, number> = {
  compact: 0.85,
  standard: 1,
};

const VALID_SCALES: UiScale[] = ["compact", "standard"];
const VALID_THEMES: UiTheme[] = ["light", "dark", "warm"];

const SCALE_STORAGE_KEY = "eplex_ui_scale";
const THEME_STORAGE_KEY = "eplex_ui_theme";

let currentZoom = 1;

function loadScale(): UiScale {
  const stored = localStorage.getItem(SCALE_STORAGE_KEY);
  if (stored && VALID_SCALES.includes(stored as UiScale)) return stored as UiScale;
  return "standard";
}

function loadTheme(): UiTheme {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && VALID_THEMES.includes(stored as UiTheme)) return stored as UiTheme;
  return "light";
}

// Save native browser APIs before any patching.
// These are the source of truth for real viewport/element dimensions.
const nativeBCR = Element.prototype.getBoundingClientRect;
const nativeClientWidthGetter = Object.getOwnPropertyDescriptor(Element.prototype, "clientWidth")?.get;
const nativeClientHeightGetter = Object.getOwnPropertyDescriptor(Element.prototype, "clientHeight")?.get;

function getRealViewportWidth(): number {
  if (window.visualViewport) {
    return window.visualViewport.width;
  }
  return nativeClientWidthGetter ? nativeClientWidthGetter.call(document.documentElement) : screen.availWidth;
}

function getRealViewportHeight(): number {
  if (window.visualViewport) {
    return window.visualViewport.height;
  }
  return nativeClientHeightGetter ? nativeClientHeightGetter.call(document.documentElement) : screen.availHeight;
}

/**
 * Patch browser APIs to compensate CSS zoom on <html>.
 *
 * Problem: CSS `zoom` on the root element scales all content, but DevExtreme
 * positions overlays via `transform: translate()` which gets further scaled
 * by zoom, causing double-scaling. Specifically:
 *
 * - `getBoundingClientRect()` returns visual (post-zoom) coordinates
 * - DevExtreme uses these to compute translate values
 * - `translate()` is applied in the zoomed coordinate space -> values are scaled again
 *
 * Fix: patch APIs to return logical (pre-zoom) coordinates so DevExtreme's math
 * produces correct translate values that, after zoom scaling, land at the right spot.
 *
 * Patched APIs:
 * - `Element.prototype.getBoundingClientRect` — position/size calculations
 * - `window.innerWidth/innerHeight` — popup centering (position.js uses these for window `of`)
 * - `document.documentElement.clientWidth/clientHeight` — collision bounds
 */
function patchBrowserAPIs(zoom: number): void {
  Object.defineProperty(window, "innerWidth", {
    get: () => Math.round(getRealViewportWidth() / zoom),
    configurable: true,
  });
  Object.defineProperty(window, "innerHeight", {
    get: () => Math.round(getRealViewportHeight() / zoom),
    configurable: true,
  });

  if (zoom === 1) {
    Element.prototype.getBoundingClientRect = nativeBCR;
    delete (document.documentElement as any).clientWidth;
    delete (document.documentElement as any).clientHeight;
  } else {
    Element.prototype.getBoundingClientRect = function () {
      const rect = nativeBCR.call(this);
      return new DOMRect(rect.x / zoom, rect.y / zoom, rect.width / zoom, rect.height / zoom);
    };

    if (nativeClientWidthGetter) {
      Object.defineProperty(document.documentElement, "clientWidth", {
        get: () => Math.round(nativeClientWidthGetter.call(document.documentElement) / zoom),
        configurable: true,
      });
    }
    if (nativeClientHeightGetter) {
      Object.defineProperty(document.documentElement, "clientHeight", {
        get: () => Math.round(nativeClientHeightGetter.call(document.documentElement) / zoom),
        configurable: true,
      });
    }
  }
}

function applyScale(scale: UiScale): void {
  const zoom = SCALE_ZOOM[scale];
  currentZoom = zoom;
  const root = document.documentElement;

  root.style.zoom = zoom === 1 ? "" : String(zoom);
  root.style.setProperty("--app-height", zoom === 1 ? "100vh" : `calc(100vh / ${zoom})`);
  root.style.setProperty("--app-width", zoom === 1 ? "100vw" : `calc(100vw / ${zoom})`);
  patchBrowserAPIs(zoom);
}

/**
 * Цвет системной строки состояния в установленном приложении. Читаем из уже
 * применённой темы, а не держим третий список цветов: строка состояния обязана
 * совпадать с шапкой кабинета, иначе на телефоне сверху висит чужая полоса.
 */
function applyThemeColor(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) return;

  const color = getComputedStyle(document.documentElement).getPropertyValue("--color-surface-secondary").trim();
  if (color) meta.setAttribute("content", color);
}

function applyTheme(theme: UiTheme): void {
  const root = document.documentElement;
  if (theme === "light") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
  applyThemeColor();
}

export function getUiZoom(): number {
  return currentZoom;
}

export const usePersonalizationStore = defineStore("personalization", {
  state: () => ({
    scale: loadScale(),
    theme: loadTheme(),
  }),

  actions: {
    setScale(scale: UiScale) {
      this.scale = scale;
      localStorage.setItem(SCALE_STORAGE_KEY, scale);
      applyScale(scale);
    },

    setTheme(theme: UiTheme) {
      this.theme = theme;
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      applyTheme(theme);
    },

    init() {
      applyScale(this.scale);
      applyTheme(this.theme);
    },
  },
});
